import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { RadioGroup } from "@/components/ui/radio-group";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Text } from "@/components/ui/text";
import { cn } from "@/lib/utils";
import type { Dictionary } from "../../../dictionaries";

interface PaymentMethodSelectorProps<T extends FieldValues> {
  control: Control<T>;
  allowedMethods: string[];
  dictionary: Dictionary;
}

export function PaymentMethodSelector<T extends FieldValues>({
  control,
  allowedMethods,
  dictionary,
}: PaymentMethodSelectorProps<T>) {
  const { checkout } = dictionary;

  const paymentMethodConfig = {
    mobile_money: {
      label: checkout.methods.mobileMoney.label,
      description: checkout.methods.mobileMoney.description,
    },
    card: {
      label: checkout.methods.card.label,
      description: checkout.methods.card.description,
    },
    qr: {
      label: checkout.methods.qr.label,
      description: checkout.methods.qr.description,
    },
  };

  if (allowedMethods.length === 0) {
    return null;
  }

  return (
    <Controller
      name={"payment_method" as Path<T>}
      control={control}
      render={({ field }) => (
        <div>
          <Text variant="small" className="font-medium mb-2">
            {checkout.paymentMethod}
          </Text>
          <div className="overflow-hidden rounded-lg border border-border">
            <RadioGroup
              name={field.name}
              value={field.value}
              onValueChange={field.onChange}
              className="gap-0"
            >
              {allowedMethods.map((method, index) => {
                const config =
                  paymentMethodConfig[
                    method as keyof typeof paymentMethodConfig
                  ];
                if (!config) return null;
                const isSelected = field.value === method;
                const isLast = index === allowedMethods.length - 1;

                return (
                  <label
                    key={method}
                    htmlFor={`payment-${method}`}
                    className={cn(
                      "flex cursor-pointer items-center justify-between px-4 py-3.5 transition-colors",
                      !isLast && "border-b border-border",
                      isSelected && "bg-muted/50",
                    )}
                  >
                    <div className="flex-1 space-y-0.5">
                      <Text variant="small" className="font-medium">
                        {config.label}
                      </Text>
                      <Text variant="muted" className="text-xs">
                        {config.description}
                      </Text>
                    </div>
                    <RadioGroupPrimitive.Item
                      value={method}
                      id={`payment-${method}`}
                      className={cn(
                        "size-4 rounded-full border-2 transition-colors",
                        isSelected
                          ? "border-primary bg-primary"
                          : "border-muted-foreground/30",
                      )}
                    >
                      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
                        <div className="size-1.5 rounded-full bg-white" />
                      </RadioGroupPrimitive.Indicator>
                    </RadioGroupPrimitive.Item>
                  </label>
                );
              })}
            </RadioGroup>
          </div>
        </div>
      )}
    />
  );
}
